import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CommonService } from '../../../../../../shared/services/common.service';
import { CustomerSharedService } from '../../../../customer-shared.service';
import { DeviceService } from '../../device.service';

import * as XLSX from 'xlsx';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-hooterManagement',
  templateUrl: './hooter.component.html',
  styleUrls: ['./hooter.component.css']
})
export class HooterManagementComponent implements OnInit {

  @ViewChild('addHooterFile', { static: false }) addHooterFile!: ElementRef;
  hooterList$: any[] = [];
  filteredHooterList$: any[] = [];

  quickFilter: 'all' | 'active' | 'inActive' = 'all';
  counts = { total_count: 0, active_count: 0, inactive_count: 0 };

  fileList: File[] = [];
  excelErr: string = '';
  excelErrLink: string = '';
  isSuccessMessage: boolean = false;

  EXPECTED_HEADERS = ['hooter_name', 'device_ui', 'model_no', 'gateway_serial', 'sensor_type', 'serial_no', 'location_id', 'last_update_user']

  isExcelModalVisible = false;
  isHooterModalVisible = false;

  isConfirmLoading = false;
  apiCallLoaders = { isHootersLoading: false };

  searchText!: string;
  searchEnabled: boolean = false;
  form!: FormGroup;
  customer_id: string;

  listOfColumns: any[] = [
    {
      name: 'STATUS',
    },
    {
      name: 'NAME',
    },
    {
      name: 'DEVEUI',
    },
    {
      name: 'MODEL NO',
    },
    {
      name: 'SERIAL NO',
    },
    {
      name: 'LOCATION',
    },

  ];

  modelNumber: any = [];
  deviceEUI: any[] = [];
  locationOptions: any[] = [];
  gatewaysByLocation: any = []

  isEditMode: boolean = false;
  editData: any = null;

  admin_name = localStorage.getItem('admin_name') || '';
  user_id = localStorage.getItem('user_id') || '';

  constructor(
    public customerSharedService: CustomerSharedService,
    public route: ActivatedRoute,
    private fb: FormBuilder,
    public commonService: CommonService,
    public deviceService: DeviceService,
    private ngZone: NgZone
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit() {
    this.getHooterbyCustomerId();
    this.getModelNumber()
    this.getDeviceEUI()
    this.getGatewaysByLocation();
    this.getLocationByCustomerId();
    this.initializeForm();
    this.filteredHooterList$ = [];
  }

  initializeForm() {
    this.form = this.fb.group({
      hooter_name: [null, Validators.required],
      location_id: [null, Validators.required],
      deviceeui: [null, Validators.required],
      model_no: [null, Validators.required],
      gateway_id: [null, Validators.required],
    });
  }

  downloadTemplate() {
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([this.EXPECTED_HEADERS]);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hooter list');
    XLSX.writeFile(wb, 'Add Hooter Template.xlsx');
  }

  onFileChange(event: any): void {
    const file = event.target.files;
    const newFiles = Array.from(file);
    for (const file of newFiles as File[]) {
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const isValidType = validExtensions.some((ext) => fileName.endsWith(ext));
      if (!isValidType) {
        this.commonService.message.error(`File ${file.name} is not an allowed file type. Please upload excel file only.`);
        continue;
      }
      this.fileList.push(file);
    }
  }

  removeFile(index: number): void {
    this.fileList.splice(index, 1);
    this.addHooterFile.nativeElement.value = '';
  }

  validateExcelFile(): boolean {
    if (this.fileList.length === 0) {
      this.commonService.message.error('Please select a file to upload');
      return false;
    }
    return true;
  }

  prepareExcelFormData(): FormData {
    const formData = new FormData();
    formData.append('xcelFile', this.fileList[0], this.fileList[0].name);
    formData.append('userId', this.user_id);
    return formData;
  }

  onExcelSubmit() {
    // Validate file selection
    if (!this.validateExcelFile()) {
      return;
    }
    this.resetExcelState();
    this.isConfirmLoading = true;

    // Prepare and submit the form data
    const formData = this.prepareExcelFormData();
    this.deviceService.addHooterExcel(formData).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        this.handleExcelUploadResponse(response);
        this.isConfirmLoading = false;
      },
      error: (error: any) => {
        this.handleExcelUploadError(error);
        this.isConfirmLoading = false;
      }
    });
  }

  resetExcelState(): void {
    this.excelErr = '';
    this.excelErrLink = '';
    this.isSuccessMessage = false;
  }

  handleExcelUploadResponse(response: any): void {
    if (response?.status === 200) {
      // Check if there's a log file URL in the response
      if (response?.result) {
        this.excelErrLink = response.result;
      }
      // Set success message
      this.isSuccessMessage = true;
      this.excelErr = 'Excel file processed successfully';

      // Only close the modal if there are no errors or warnings
      if (!this.excelErrLink) {
        this.isExcelModalVisible = false;
        this.fileList = []; // Clear the file list
      }
      this.getHooterbyCustomerId() // Refresh the asset list
    } else {
      // Handle error responses
      this.isSuccessMessage = false;
      this.excelErr = response?.message || response?.error_message || 'Failed to process excel file';
      if (response?.log_file_url) {
        this.excelErrLink = response.log_file_url;
      }
      this.commonService.message.error(this.excelErr);
    }
  }

  handleExcelUploadError(error: any): void {
    this.isSuccessMessage = false;
    this.excelErr = error?.error?.message || 'An error occurred while uploading the excel file';
    if (error?.error?.log_file_url) {
      this.excelErrLink = error.error.log_file_url;
    }
    this.commonService.message.error(this.excelErr);
    console.error('Error uploading excel:', error);
  }

  downloadLogFile() {
    if (this.excelErrLink) {
      window.open(this.excelErrLink, '_blank');
    }
  }

  getModelNumber() {
    this.deviceService.getModelNumber().pipe(untilDestroyed(this)).subscribe((response) => {
      if (response?.status === 200) {
        this.modelNumber = response?.data;
      } else {
        this.commonService.message.error(response?.message || 'Failed to fetch model numbers');
      }
    }, (error) => {
      this.commonService.message.error(error?.error?.message || 'An error occurred while fetching model numbers');
      console.error('Error fetching model numbers:', error);
    });
  }

  getDeviceEUI() {
    console.log("Fetching device EUI list");
    this.deviceService.getDeviceEUI().pipe(untilDestroyed(this)).subscribe({
      next: (response) => {
        if (response?.status === 200 && response?.result) {
          this.deviceEUI = response.result;
          console.log("Device EUI list fetched successfully:", this.deviceEUI);
        } else {
          this.deviceEUI = [];
          console.warn("Failed to fetch device EUI or empty result:", response);
          this.commonService.message.error(response?.message || 'Failed to fetch device EUI');
        }
      },
      error: (error) => {
        this.deviceEUI = [];
        console.error('Error fetching device EUI:', error);
        this.commonService.message.error(error?.error?.message || 'An error occurred while fetching device EUI');
      }
    });
  }

  getGatewaysByLocation() {
    const obj = {
      group_id: this.customer_id
    }
    this.deviceService.getGatewaysByLocation(obj).pipe(untilDestroyed(this)).subscribe((response) => {
      if (response && response.data) {
        this.gatewaysByLocation = response.data;
      }
    });
  }

  getLocationByCustomerId() {
    this.commonService.getLocationList(Number(this.customer_id)).subscribe({
      next: (response: any) => {
        const locations = Array.isArray(response?.data) ? response.data : [];
        this.locationOptions = locations;
      },
      error: (err) => {
        console.error('Error fetching locations', err);
        this.commonService.message.error('Failed to fetch locations');
      }
    })
  };

  onSubmit() {
    if (this.form.valid) {
      this.isConfirmLoading = true;
      const formData = this.form.value;
      formData['last_update_user'] = this.admin_name;
      formData['user_id'] = this.user_id;

      if (this.isEditMode && this.editData) {
        const idt_hooter = this.editData.idt_hooter;

        if (idt_hooter) {
          formData['idt_hooter'] = idt_hooter;
        }
      }

      const apiCall = this.isEditMode ?
        this.deviceService.updateHooter(formData) :
        this.deviceService.addHooter(formData)

      apiCall.pipe(untilDestroyed(this)).subscribe({
        next: (response: any) => {
          if (response?.status === 200) {
            this.commonService.message.success(
              this.isEditMode ? 'Hooter updated successfully' : 'Hooter added successfully'
            );
            this.isHooterModalVisible = false;
            this.getHooterbyCustomerId();
          } else {
            this.commonService.message.error(
              response?.message || (this.isEditMode ? 'Failed to update hooter' : 'Failed to add hooter')
            );
          }
          this.isConfirmLoading = false;
        },
        error: (error: any) => {
          this.commonService.message.error(
            this.isEditMode ? 'An error occurred while updating the hooter' : 'An error occurred while adding the hooter'
          );
          console.error('Error:', error);
          this.isConfirmLoading = false;
        }
      });
    }
  }

  getHooterbyCustomerId() {
    this.counts = { total_count: 0, active_count: 0, inactive_count: 0 };
    this.ngZone.runOutsideAngular(() => {
      this.deviceService.getHooterbyCustomerId(this.customer_id)
        .pipe(untilDestroyed(this))
        .subscribe((response) => {
          if (response?.result && response?.status === 200) {
            this.hooterList$ = response?.result;
            this.filteredHooterList$ = [...this.hooterList$];

            this.hooterList$.forEach((hooter) => {
              this.counts.total_count++;
              if (hooter?.is_active === true) this.counts.active_count++;
              if (hooter?.is_active === false) this.counts.inactive_count++;
            });
            this.getSearchDetails('');
            this.filteredHooterList$ = [...this.hooterList$];

            this.ngZone.run(() => {
              this.apiCallLoaders.isHootersLoading = false;
            });
          } else {
            this.ngZone.run(() => {
              this.apiCallLoaders.isHootersLoading = false;
            });
          }
        });
    });
  }

  filterData(): void {
    if (this.quickFilter === 'all') {
      this.filteredHooterList$ = this.hooterList$;
    } else if (this.quickFilter === 'active') {
      this.filteredHooterList$ = this.hooterList$.filter(
        (hooter) => hooter.is_active
      );
    } else if (this.quickFilter === 'inActive') {
      this.filteredHooterList$ = this.hooterList$.filter(
        (hooter) => !hooter.is_active
      );
    }
  }
  getSearchDetails(val: string): void {
    let searchText = val.trim().toLowerCase();
    this.filteredHooterList$ = this.hooterList$.filter(hooter => {
      return (hooter.hooter_name && hooter.hooter_name.toLowerCase().includes(searchText)) ||
        (hooter.deviceeui.toString() && hooter.deviceeui.toString().includes(searchText)) ||
        (hooter.display_name && hooter.display_name.toLowerCase().includes(searchText)) ||
        (hooter.model_number && hooter.model_number.toLowerCase().includes(searchText)) ||
        (hooter.serial_number.toString() && hooter.serial_number.toString().includes(searchText))
    });
  }

  refreshHandler(): void {
    this.hooterList$ = [];
    this.filteredHooterList$ = [];
    this.quickFilter = 'all';
    this.searchText = '';
    this.searchEnabled = false;
    this.getHooterbyCustomerId();
  }

  showModal(type: string): void {
    if (type === 'excel') {
      this.isExcelModalVisible = true;
    } else if (type === 'hooter') {
      this.isEditMode = false;
      this.editData = null;
      this.form.reset();
      this.isHooterModalVisible = true;
    }
  }

  editHooter(hooter: any): void {
    console.log("Editing hooter:", hooter);

    this.isEditMode = true;
    this.editData = hooter;

    if (hooter.deviceeui) {
      const deviceEuiExists = this.deviceEUI.some(
        (device: any) => device.device_ui === hooter.deviceeui
      );
      // If not in the list, add it temporarily for selection
      if (!deviceEuiExists && hooter.deviceeui) {
        this.deviceEUI = [
          ...this.deviceEUI,
          {
            device_ui: hooter.deviceeui
          }
        ];
      }
    }

    // Patch the form with values from the selected hooter
    const formValues = {
      hooter_name: hooter.hooter_name,
      model_no: hooter.model_number,
      deviceeui: hooter.deviceeui,
      gateway_id: hooter.gateway_id,
      location_id: hooter.location_id
    };
    this.form.patchValue(formValues);

    this.isHooterModalVisible = true;
  }

  deleteHooter(idt_hooter: string) {
    const obj = {
      hooterId: idt_hooter
    }
    this.deviceService.deleteHooter(obj).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        this.commonService.message.success('Hooter deleted successfully');
        this.getHooterbyCustomerId();
      },
      error: (error: any) => {
        this.commonService.message.error('Failed to delete hooter');
        console.error('Error deleting hooter:', error);
      }
    });
  }

  handleCancel(type: string): void {
    if (type === 'excel') {
      this.isExcelModalVisible = false;
    } else if (type === 'hooter') {
      this.isHooterModalVisible = false;
      this.form.reset();
      this.isEditMode = false;
      this.editData = null;
    }
  }
}
